import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import firebaseApp from '../../../firebase-app';
import { SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import { collection, doc, updateDoc } from 'firebase/firestore';
import svStrings from '../../../sv-strings';
import { useSnackbar } from 'notistack';
import { MappingLeagues } from './mappings-list';

const styles: SxStyle = {
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

const parseNumbers = (str: string | undefined) => {
  try {
    return (
      str
        ?.split?.(',')
        .filter((x) => x)
        .map((num: string) => parseInt(num)) || str
    );
  } catch (error) {
    return [];
  }
};

const MappingsListLeagues = () => {
  const { strings } = useStringsContext();
  const [rows, setRows] = useState<MappingLeagues[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const [data, loading, error] = useCollectionData(
    collection(firebaseApp.db, `mappings/leagues/all`)
  );

  useEffect(() => {
    if (data && !loading && !error) {
      setRows(data as MappingLeagues[]);
    }
  }, [data, error, loading]);

  const processRowUpdate = useCallback(
    async (row: any) => {
      const updatedRow = {
        ...row,
        fogisIds: parseNumbers(row.fogisIds),
        everysportIds: parseNumbers(row.everysportIds),
        teams: row.teams?.split?.(',').filter((x: string) => x) || row.teams,
      };
      try {
        await updateDoc(doc(firebaseApp.db, `mappings/leagues/all/${row.id}`), updatedRow);
      } catch (error) {
        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
      return row;
    },
    [enqueueSnackbar, strings]
  );

  return (
    <DataGrid
      rows={rows}
      getRowId={(row) => row.id}
      columns={columns}
      processRowUpdate={processRowUpdate}
      loading={loading}
      autoPageSize
    />
  );
};

const columns: GridColDef[] = [
  {
    field: 'id',
    headerName: svStrings.mappings.id,
    width: 180,
    editable: false,
  },
  {
    field: 'configLeagueName',
    headerName: svStrings.mappings.name,
    minWidth: 130,
    flex: 1,
    editable: true,
  },
  {
    field: 'leagueDisplayName',
    headerName: svStrings.mappings.displayName,
    minWidth: 130,
    flex: 1,
    editable: true,
  },
  {
    field: 'configSeasonStartYear',
    headerName: svStrings.mappings.leagueStartYear,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'everysportLeagueId',
    headerName: svStrings.mappings.everySportLeagueId,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'everysportIds',
    headerName: svStrings.mappings.everySportIds,
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'fogisIds',
    headerName: svStrings.mappings.fogisIds,
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'smcId',
    headerName: svStrings.mappings.smcId,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'teams',
    headerName: svStrings.mappings.teams,
    minWidth: 100,
    flex: 1,
    editable: true,
  },
];

export default MappingsListLeagues;
